import { isEmpty, keyBy } from "lodash"
import { useMemo } from "react"

import { evalTraitAtoms, evaluationAtoms } from "#atoms"
import { NULL_ID } from "#classes/Firebase"
import { CLASS_GROUPS } from "#constants/classOptions"
import {
  EvaluationConstructor,
  EvaluationTrait,
} from "#customTypes/composedTypes/evaluationTypes"
import {
  COMPARATORS,
  DOC_TYPES,
} from "#customTypes/composedTypes/firebaseTypes"
import { Uuid } from "#customTypes/globalTypes"
import useCombinedReady from "#hooks/dataHooks/useCombinedReady"
import { useFilteredDocs, useSomeDocs } from "#hooks/jotai/useSubscription"
import { findPreviousEval } from "#utils/evaluationHelpers"

const usePreviousEval = (
  studentId: Uuid,
  classGroup: CLASS_GROUPS,
  currentSemester: string
) => {
  const evalQuery = useMemo(() => {
    return {
      queries: {
        studentId: {
          value: studentId,
          comparator: COMPARATORS.EQUALS,
        },
        classGroup: {
          value: classGroup,
          comparator: COMPARATORS.EQUALS,
        },
      },
    }
  }, [studentId, classGroup])

  const evaluations = useFilteredDocs<EvaluationConstructor>(
    evaluationAtoms,
    DOC_TYPES.EVALUATIONS,
    evalQuery
  )

  // For Jotai, we need to track pending/error differently
  // Since useFilteredDocs doesn't return these, we'll assume ready if we have data
  const isEvaluationsPending =
    evaluations.length === 0 && evalQuery.queries.studentId.value != null
  const evaluationsError: Error | null = null

  const previousEval = useMemo(
    () => findPreviousEval(evaluations, currentSemester),
    [evaluations, currentSemester]
  )

  const previousEvalTraitIds = useMemo(() => {
    if (isEmpty(previousEval?.checkboxes)) return [NULL_ID]
    return Object.keys(previousEval.checkboxes)
  }, [previousEval])

  // For IN queries with IDs, use useSomeDocs instead of useFilteredDocs
  // Filter out NULL_ID since it's not a real ID
  const validTraitIds = useMemo(() => {
    return previousEvalTraitIds.filter((id) => id !== NULL_ID)
  }, [previousEvalTraitIds])

  const previousEvalTraits = useSomeDocs<EvaluationTrait>(
    evalTraitAtoms,
    DOC_TYPES.EVAL_TRAITS,
    validTraitIds
  )

  // For Jotai, we need to track pending/error differently
  const isPreviousEvalTraitsPending =
    previousEvalTraits.length === 0 && validTraitIds.length > 0
  const previousEvalTraitsError: Error | null = null

  const previousEvalTraitsById = useMemo(() => {
    return keyBy(previousEvalTraits, "id")
  }, [previousEvalTraits])

  const { isReady, errors } = useCombinedReady([
    {
      isPending: isEvaluationsPending,
      isActive: true,
      error: evaluationsError,
      source: "evaluations",
    },
    {
      isPending: isPreviousEvalTraitsPending,
      isActive: !isEmpty(previousEval?.checkboxes),
      error: previousEvalTraitsError,
      source: "previousEvalTraits",
    },
  ])

  return {
    previousEval,
    previousEvalTraitsById,
    isReady,
    errors,
  }
}

export default usePreviousEval
